import { Component, EventEmitter, OnInit, OnDestroy, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SavedDataInfo } from '../../../../models/form/saved-data-info';
import { AuditLog } from '../../../../models/audit-log.model';
import { RegulationsService } from '../../../../services/api/general-settings/regulations.service';
import { RegulationsFormComponent } from '../components/regulations-form/regulations-form.component';
import { TranslationsDetailsComponent } from '../components/translations-details/translations-details.component';
import { RegulationsCategoriesComponent } from '../components/regulations-categories/regulations-categories.component';
import { RegulationsChangesLogComponent } from '../components/regulations-logs-change/regulations-changes-logs.component';

import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { FormService } from 'src/app/services/form.service';
import { AlertInfo } from 'src/app/models/alert-info';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-regulations-edit-page',
  imports: [RegulationsFormComponent, TranslationsDetailsComponent, RegulationsCategoriesComponent, RegulationsChangesLogComponent, NgbAlert],
  templateUrl: './regulations-edit-page.component.html',
  styleUrl: './regulations-edit-page.component.scss'
})
export class RegulationsEditPageComponent implements OnInit, OnDestroy {
  @ViewChild(RegulationsFormComponent) regulationFormComponent!: RegulationsFormComponent;
  @ViewChild(TranslationsDetailsComponent) translationsComponent!: TranslationsDetailsComponent;
  @ViewChild(RegulationsCategoriesComponent) categoriesComponent!: RegulationsCategoriesComponent;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  regulationId!: number;
  regulation?: any;
  translations?: any;
  category?: any;
  auditLogs: AuditLog[] = [];
  readonly: boolean;
  isSubmitting: boolean = false;
  isLoadingAuditLogs: boolean = false;
  form!: FormGroup;

  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private regulationsService: RegulationsService,
    private alertNotificationService: AlertNotificationService,
    private formService: FormService,
    private fb: FormBuilder
  ) {
    this.readonly = false;
    this.initForm();
  }

  
  initForm(): void {
    this.form = this.fb.group({
      regulationId: [null],
      lawId: [null, Validators.required],
      title: ['', Validators.required],
      details: ['', Validators.required],
      categoryIds: [[]],
      regulationArticles: this.fb.array([])
    });
  }

  addRegulationArticle(languageId: number, article: string): void {
    const articleGroup = this.fb.group({
      languageId: [languageId, Validators.required],
      article: [article, Validators.required]
    });
    this.regulationArticlesArray.push(articleGroup);
  }

  get regulationArticlesArray(): FormArray {
    return this.form.get('regulationArticles') as FormArray;
  }

  ngOnInit(): void {
    this.regulationId = Number(this.route.snapshot.paramMap.get('regulationId'));
    if (this.regulationId) {
      this.loadData(this.regulationId);
      this.loadAuditLogs(this.regulationId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData(regulationId: number): void {
    this.regulationsService.detail(regulationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.regulation = response.data?.regulation;
          this.translations = response.data?.regulation?.regulationArticles;
          this.category = response.data?.regulation?.categories;

          if (this.regulation) {
            this.form.patchValue({
              regulationId: this.regulation.id,
              lawId: this.regulation.law?.lawId,
              title: this.regulation.title,
              details: this.regulation.details
            });

            if (this.category && this.category.length > 0) {
              const categoryIds = this.category.map((cat: any) => cat.categoryId);
              this.form.get('categoryIds')?.setValue(categoryIds);
            }

            if (this.translations && this.translations.length > 0) {
              while (this.regulationArticlesArray.length) {
                this.regulationArticlesArray.removeAt(0);
              }

              this.translations.forEach((trans: any) => {
                this.addRegulationArticle(trans.language.languageId, trans.article);
              });
            }
          }
        },
        error: (error: any) => {
          console.error('Error loading data:', error);
          this.alertNotificationService.error('Error loading data');
        }
      });
  }

  loadAuditLogs(regulationId: number): void {
    this.isLoadingAuditLogs = true;
    this.regulationsService.getChangesLog(regulationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.auditLogs = response.data?.auditLogs || [];
          this.isLoadingAuditLogs = false;
        },
        error: (error: any) => {
          console.error('Error loading changelog:', error);
          this.auditLogs = [];
          this.isLoadingAuditLogs = false;
        }
      });
  }

  onRegulationFormSavedData(savedDataInfo: SavedDataInfo): void {
    if (savedDataInfo.success && this.regulationId) {
      this.loadData(this.regulationId);
      this.loadAuditLogs(this.regulationId);
    }
  }

  onRegulationCategoriesSavedData(savedDataInfo: SavedDataInfo): void {
    if (savedDataInfo.success && this.regulationId) {
      this.loadData(this.regulationId);
      this.loadAuditLogs(this.regulationId);
    }
  }

  resetAllAlerts(): void {
    this.alertInfo.show = false;
    this.regulationFormComponent.alertInfo.show = false;
    this.translationsComponent.alertInfo.show = false;
    this.categoriesComponent.alertInfo.show = false;
  }

  distributeErrorsToComponents(validationErrors: any[]): void {
    const regulationFormFields = ['jurisdictionId', 'lawId', 'title', 'details'];
    const translationsFields = ['regulationArticles'];
    const categoriesFields = ['categoryIds'];

    const regulationFormErrorMsgs: string[] = [];
    const translationsErrorMsgs: string[] = [];
    const categoriesErrorMsgs: string[] = [];

    validationErrors.forEach(error => {
      const fieldName = error.field;
      const errorMsg = error.defaultMessage || 'Invalid field';

      if (regulationFormFields.some(field => fieldName.includes(field))) {
        if (fieldName.includes('lawId')) {
          regulationFormErrorMsgs.push(`Law: ${errorMsg}`);
        } else if (fieldName.includes('jurisdictionId')) {
          regulationFormErrorMsgs.push(`Jurisdiction: ${errorMsg}`);
        } else if (fieldName.includes('title')) {
          regulationFormErrorMsgs.push(`Title: ${errorMsg}`);
        } else if (fieldName.includes('details')) {
          regulationFormErrorMsgs.push(`Details: ${errorMsg}`);
        } else {
          regulationFormErrorMsgs.push(errorMsg);
        }
      }

      if (translationsFields.some(field => fieldName.includes(field))) {
        if (fieldName.includes('[0]') || fieldName.includes('eng')) {
          translationsErrorMsgs.push(`English translation: ${errorMsg}`);
        } else if (fieldName.includes('[1]') || fieldName.includes('ita')) {
          translationsErrorMsgs.push(`Italian translation: ${errorMsg}`);
        } else if (fieldName.includes('[2]') || fieldName.includes('spa')) {
          translationsErrorMsgs.push(`Spanish translation: ${errorMsg}`);
        } else {
          translationsErrorMsgs.push(`Translation: ${errorMsg}`);
        }
      }

      if (categoriesFields.some(field => fieldName.includes(field))) {
        categoriesErrorMsgs.push(`Category: ${errorMsg}`);
      }
    });

    if (regulationFormErrorMsgs.length > 0) {
      this.regulationFormComponent.showAlertInfo('danger',
        `Errors in regulation:<br>${regulationFormErrorMsgs.map(msg => `- ${msg}`).join('<br>')}`);
    }

    if (translationsErrorMsgs.length > 0) {
      this.translationsComponent.showAlertInfo('danger',
        `Errors in translations:<br>${translationsErrorMsgs.map(msg => `- ${msg}`).join('<br>')}`);
    }

    if (categoriesErrorMsgs.length > 0) {
      this.categoriesComponent.showAlertInfo('danger',
        `Errors in categories:<br>${categoriesErrorMsgs.map(msg => `- ${msg}`).join('<br>')}`);
    }
  }

  /**
   * *******************
   * Submit form methods
   * *******************
   */
  showAlertInfo(type: string, message: string): void {
    this.alertInfo = {
      show: true,
      type,
      message
    };
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }

  updateValidationErrors(validationErrors: any): void {
    this.formService.resetErrors(this.form);
    this.formService.setValidationErrors(this.form, validationErrors);
  }

  submitFormSuccess(response: any): void {
    this.alertNotificationService.success('Operation completed successfully');
    this.showAlertInfo('success', 'Operation completed successfully');

    this.onSavedData.emit({
      success: true,
      response
    });
  }

  submitFormError(error: any): void {
    if (error.error.error.description === 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    }
    else if (error.error.error.description === 'USER_EMAIL_ALREADY_EXISTS') {
      this.updateValidationErrors([{ field: 'email', defaultMessage: error.error.error.message }]);
      this.showAlertInfo('danger', `Please fix the error: ${error.error.error.message}`);
    }
    else {
      this.showAlertInfo('danger', error.error.error.message);
    }

    this.onSavedData.emit({
      success: false,
      error
    });
  }

  submitForm(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    this.resetAllAlerts();

    const isRegulationFormValid = this.regulationFormComponent.form.valid;
    const isTranslationsFormValid = this.translationsComponent.form.valid;
    const isCategoriesFormValid = this.categoriesComponent.form.valid;

    if (!isRegulationFormValid || !isTranslationsFormValid || !isCategoriesFormValid) {
      this.showAlertInfo('danger', 'There are errors in the forms. Please correct the highlighted errors.');

      if (!isRegulationFormValid) {
        this.regulationFormComponent.form.markAllAsTouched();
        this.regulationFormComponent.showAlertInfo('danger', 'Please check the regulation fields. All fields are required.');
      }

      if (!isTranslationsFormValid) {
        this.translationsComponent.form.markAllAsTouched();
        this.translationsComponent.showAlertInfo('danger', 'Translations are incomplete. Please verify that all required fields are filled.');
      }

      if (!isCategoriesFormValid) {
        this.categoriesComponent.form.markAllAsTouched();
        this.categoriesComponent.showAlertInfo('danger', 'You must select at least one category.');
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.isSubmitting = false;
      return;
    }

    const regulationData = this.regulationFormComponent.form.value;
    const translationsData = this.translationsComponent.form.value;
    const categoryIds = this.categoriesComponent.selectedCategoryIds.map(id => parseInt(id, 10));

    this.form.patchValue({
      regulationId: this.regulationId || undefined,
      lawId: regulationData.law,
      title: regulationData.title,
      details: regulationData.details,
      categoryIds: categoryIds
    });

    while (this.regulationArticlesArray.length) {
      this.regulationArticlesArray.removeAt(0);
    }

    if (translationsData.eng) {
      this.addRegulationArticle(146, translationsData.eng);
    }
    if (translationsData.ita) {
      this.addRegulationArticle(230, translationsData.ita);
    }
    if (translationsData.spa) {
      this.addRegulationArticle(480, translationsData.spa);
    }

    if (this.form.valid) {
      const payload = { ...this.form.value };

      if (this.regulationId) {
        payload.regulationId = this.regulationId;
      } else {
        delete payload.regulationId;
      }

      console.log('Payload inviato al server:', payload);

      this.alertInfo.show = false;
      this.formService.resetErrors(this.form);

      const request = this.regulationId
        ? this.regulationsService.update(this.regulationId, payload)
        : this.regulationsService.create(payload);

      request.pipe(takeUntil(this.destroy$)).subscribe({
        next: (response) => {
          this.alertNotificationService.success('Regulation successfully saved');
          this.submitFormSuccess(response);
          if (!this.regulationId && response.data?.regulation?.id) {
            this.router.navigate(['/app/general-settings/regulations/update', response.data.regulation.id]);
          } else {
            this.loadData(this.regulationId);
            this.loadAuditLogs(this.regulationId);
          }
          this.isSubmitting = false;
        },
        error: (error) => {
          if (error.error?.error?.description === 'ARGUMENT_VALIDATION_ERROR' && error.error?.error?.validationErrors) {
            this.updateValidationErrors(error.error.error.validationErrors);

            const totalErrorCount = error.error.error.validationErrors.length;
            const formMessage = `${totalErrorCount} validation error${totalErrorCount > 1 ? 's' : ''} detected.<br>Please check the specific details in the forms below.`;
            this.showAlertInfo('danger', formMessage);

            const validationErrors = error.error.error.validationErrors;
            this.distributeErrorsToComponents(validationErrors);
          } else {
            const errorMessage = error.error?.error?.message || 'Unknown error during save';
            this.alertNotificationService.error('Error during save: ' + errorMessage);
            this.showAlertInfo('danger', `An error occurred: ${errorMessage}`);
            this.submitFormError(error);
          }
          this.isSubmitting = false;
        }
      });
    } else {
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });
      this.alertNotificationService.error('The form contains errors. Please check your input.');
      this.isSubmitting = false;
    }
  }
  /**
   * *******************
   * Submit form methods
   * *******************
   */
}
